/**
 * **plain テーマの「盤に置かれているもの」**（Issue #523）。**文字だけ**。
 *
 * arcade と違うのは（＝契約の検算）:
 * - カードは**画像を貼らず名前の文字**（枠と文字だけ）
 * - **平ら**（厚み 0・影なし・浮きなし）。器が「厚みのない板」を扱えているかが目で分かる
 * - **`liveHand` を持たない**（＝実写の手札は描かれない）
 */
import { fitText, roundRectPath } from '../../canvas/paint';
import { PLAYER_COLORS } from '../../core/hud';

/** カードの幅に対する比（元の CSS の px ÷ 130） */
const R = Object.freeze({ radius: 0.038, border: 0.023, name: 0.154, pad: 0.031 });

const FACE = { border: 'rgba(255,255,255,0.85)', bg: 'rgba(10,14,24,0.72)' };
const DOWN = { border: 'rgba(180,160,255,0.85)', bg: 'rgba(40,28,64,0.82)' };

function paint(ctx, { size }, { faceUp, name, badge }) {
  const { w, h } = size;
  const b = w * R.border;
  const r = w * R.radius;
  const look = faceUp ? FACE : DOWN;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = look.bg;
  roundRectPath(ctx, b / 2, b / 2, w - b, h - b, r);
  ctx.fill();
  ctx.lineWidth = b;
  ctx.strokeStyle = look.border;
  ctx.stroke();

  ctx.fillStyle = '#fff';
  if (name) {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    fitText(ctx, name, w - b * 4, w * R.name);
    ctx.fillText(name, w / 2, h / 2);
  }
  if (badge != null) {
    ctx.font = `700 ${w * R.name}px sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`×${badge}`, w - w * R.pad - b, h - w * R.pad - b);
  }
}

const nameOf = (card, scene) => (card?.faceUp
  ? (card.code ? (scene?.cards?.name?.(card.code) ?? String(card.code)) : '')
  : '裏');

/** **平らな板**（厚みも影も持たない＝器が扱えているかの検算） */
const FLAT = Object.freeze({ lift: 0, thickness: 0, shadow: false });

export function card({ item, scene }) {
  const c = item.card;
  const name = nameOf(c, scene);
  return {
    ...FLAT,
    draw: (ctx, env) => paint(ctx, env, { faceUp: c.faceUp, name, badge: null }),
  };
}

export function pile({ item, scene }) {
  const top = item.top;
  const name = top ? nameOf(top, scene) : '';
  return {
    ...FLAT,
    draw: (ctx, env) => paint(ctx, env, { faceUp: !!top?.faceUp, name, badge: item.count }),
  };
}

/** **盤の縁**。arcade は光らせるが、こちらは**細い線 1 本**（文字と線だけのテーマ） */
export function frame({ item, scene, view, canvas }) {
  const outline = view?.outline;
  if (!outline || !item.turn || !canvas) return null;
  const color = scene?.players?.[item.player]?.color ?? PLAYER_COLORS[item.player] ?? PLAYER_COLORS[0];
  const pad = 8;
  const xs = outline.map((p) => p[0]); const ys = outline.map((p) => p[1]);
  const x = Math.max(0, Math.min(...xs) - pad); const y = Math.max(0, Math.min(...ys) - pad);
  const box = {
    x, y, w: Math.min(canvas.w, Math.max(...xs) + pad) - x, h: Math.min(canvas.h, Math.max(...ys) + pad) - y,
  };
  if (!(box.w > 0) || !(box.h > 0)) return null;
  const pts = outline.map(([px, py]) => [px - x, py - y]);
  return {
    box,
    bitmap: { w: Math.round(box.w), h: Math.round(box.h) },
    deps: [color, ...outline.flat()],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      pts.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
      ctx.closePath();
      ctx.stroke();
    },
  };
}

/** 種類 → 見た目を返す純関数。**`liveHand` は持たない**（＝描かれない） */
export default Object.freeze({ card, pile, frame });
